import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/api-client";
import { handleApiError } from "../api/error-handler";

export interface TranslationLanguage {
  id: number;
  code: string;
  name: string;
  isDefault: boolean;
}

export interface TranslationEntry {
  propertyName: string;
  value: string;
  isAiTranslated?: boolean;
  isStale?: boolean;
}

export interface EntityTranslationResponse {
  entityType: string;
  entityId: number;
  translations: TranslationEntry[];
}

/**
 * Hook to fetch supported translation languages
 */
export const useTranslationLanguages = () => {
  return useQuery<TranslationLanguage[], Error>({
    queryKey: ["translationLanguages"],
    queryFn: async () => {
      try {
        const response = await apiClient.get<TranslationLanguage[]>("/translations/languages");
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to fetch translation languages");
      }
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
};

/**
 * Query for an entity's cells in one language. Every reader must share this key: the
 * post-save refresh (scheduleTranslationRefresh) invalidates by its prefix.
 */
export const entityTranslationsQuery = (
  entityType: string,
  entityId: number | undefined,
  languageCode: string
) => ({
  queryKey: ["entityTranslations", entityType, entityId, languageCode],
  queryFn: async (): Promise<Record<string, string>> => {
    try {
      const response = await apiClient.get<EntityTranslationResponse>(
        `/translations/entity/${entityType}/${entityId}?languageCode=${languageCode}`
      );
      const result: Record<string, string> = {};
      response.data.translations?.forEach((t) => {
        result[t.propertyName] = t.value;
      });
      return result;
    } catch (error) {
      throw handleApiError(error, "Failed to fetch entity translations");
    }
  },
});

/**
 * Hook to fetch translations for a specific entity and language
 */
export const useEntityTranslations = (
  entityType: string,
  entityId: number | undefined,
  languageCode: string,
  refetchInterval: number | false = false,
  enabled: boolean = true
) => {
  return useQuery<Record<string, string>, Error>({
    ...entityTranslationsQuery(entityType, entityId, languageCode),
    enabled: enabled && !!entityId && !!languageCode,
    staleTime: 0, // always refetch on mount — these cells change via saves and the async worker
    refetchInterval,
  });
};

export interface EntityTranslationFlags {
  /** Property names whose human-authored translation is stale (the source changed since). */
  staleFields: Set<string>;
  /** Property names that have a human-authored cell in this language (i.e. NOT AI-managed). */
  humanManagedFields: Set<string>;
}

/**
 * Per-field translation flags for a language: which are stale, and which are human-managed
 * (vs. left to the AI). One call; kept separate from useEntityTranslations' value-only shape.
 */
export const useEntityTranslationFlags = (
  entityType: string,
  entityId: number | undefined,
  languageCode: string,
  refetchInterval: number | false = false,
  enabled: boolean = true
) => {
  return useQuery<EntityTranslationFlags, Error>({
    queryKey: ["entityTranslationFlags", entityType, entityId, languageCode],
    queryFn: async () => {
      try {
        const response = await apiClient.get<EntityTranslationResponse>(
          `/translations/entity/${entityType}/${entityId}?languageCode=${languageCode}`
        );
        const staleFields = new Set<string>();
        const humanManagedFields = new Set<string>();
        response.data.translations?.forEach((t) => {
          if (t.isStale) staleFields.add(t.propertyName);
          if (t.isAiTranslated === false) humanManagedFields.add(t.propertyName);
        });
        return { staleFields, humanManagedFields };
      } catch (error) {
        throw handleApiError(error, "Failed to fetch translation flags");
      }
    },
    enabled: enabled && !!entityId && !!languageCode,
    staleTime: 0, // always refetch on mount — these cells change via saves and the async worker
    refetchInterval,
  });
};

/**
 * Languages an entity can be translated into (empty when none — used to hide the auto-translate toggle).
 */
export const useTargetLanguages = (
  entityType: string,
  entityId: number | undefined,
  enabled: boolean = true
) => {
  return useQuery<string[], Error>({
    queryKey: ["targetLanguages", entityType, entityId],
    queryFn: async () => {
      try {
        const response = await apiClient.get<string[]>(
          `/translations/entity/${entityType}/${entityId}/target-languages`
        );
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to fetch target languages");
      }
    },
    enabled: enabled && !!entityId,
    staleTime: 5 * 60 * 1000,
  });
};

export interface RegenerateTranslationVars {
  entityType: string;
  entityId: number;
  propertyName: string;
  languageCode: string;
}

/**
 * Hands a field back to the AI: drops the human-authored cell server-side and re-triggers
 * translation. The worker translates asynchronously, so the new value lands on a later refetch.
 */
export const useRegenerateTranslation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RegenerateTranslationVars) => {
      await apiClient.post("/translations/entity/regenerate", vars);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["entityTranslationFlags", vars.entityType, vars.entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ["entityTranslations", vars.entityType, vars.entityId],
      });
    },
  });
};
